import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazyWithRetry } from "@dwelldrop/shared";
import ErrorBoundary from "./components/ErrorBoundary";
import { VenueAuthProvider } from "./contexts/VenueAuthContext";
import { VenueProvider } from "./contexts/VenueContext";
import ProtectedRoute from "./components/ProtectedRoute";
import VenueAccessGate from "./components/VenueAccessGate";
import VenueLayout from "./components/VenueLayout";
import RouteAnnouncer from "./components/RouteAnnouncer";
import NavigationProgress from "./components/NavigationProgress";
import LoadingSpinner from "./components/LoadingSpinner";
import NewVersionBanner from "./components/NewVersionBanner";
import NewVersionModal from "./components/NewVersionModal";
import "./index.css";

// Route-level code splitting — auto-reloads on stale chunks after deploy
const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const Verify2FAPage = lazyWithRetry(() => import("./pages/Verify2FAPage"));
const EmailVerifiedPage = lazyWithRetry(
  () => import("./pages/EmailVerifiedPage")
);
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"));
const DealsPage = lazyWithRetry(() => import("./pages/DealsPage"));
const CreateDealPage = lazyWithRetry(() => import("./pages/CreateDealPage"));
const EditDealPage = lazyWithRetry(() => import("./pages/EditDealPage"));
const DealDetailPage = lazyWithRetry(() => import("./pages/DealDetailPage"));
const QRCodesPage = lazyWithRetry(() => import("./pages/QRCodesPage"));
const CreateQRCodePage = lazyWithRetry(
  () => import("./pages/CreateQRCodePage")
);
const StaffPage = lazyWithRetry(() => import("./pages/StaffPage"));
const ScannerPage = lazyWithRetry(() => import("./pages/ScannerPage"));
const DisputesPage = lazyWithRetry(() => import("./pages/DisputesPage"));
const RewardsPage = lazyWithRetry(() => import("./pages/RewardsPage"));
const CommissionDashboardPage = lazyWithRetry(
  () => import("./pages/CommissionDashboardPage")
);
const LocationAnalyticsPage = lazyWithRetry(
  () => import("./pages/LocationAnalyticsPage")
);
const VenueAnalyticsPage = lazyWithRetry(
  () => import("./pages/VenueAnalyticsPage")
);
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const Setup2FAPage = lazyWithRetry(() => import("./pages/Setup2FAPage"));
const InvoicesPage = lazyWithRetry(() => import("./pages/InvoicesPage"));
const InvoiceDetailPage = lazyWithRetry(
  () => import("./pages/InvoiceDetailPage")
);
/*
const AIStudioPage = lazyWithRetry(() => import("./pages/AIStudioPage"));
const ContentPage = lazyWithRetry(() => import("./pages/ContentPage"));
const CreatePostPage = lazyWithRetry(() => import("./pages/CreatePostPage"));
const PostDetailPage = lazyWithRetry(() => import("./pages/PostDetailPage"));
const EditPostPage = lazyWithRetry(() => import("./pages/EditPostPage"));
*/
// const ReferralDashboardPage = lazyWithRetry(() => import("./pages/ReferralDashboardPage"));
const MarketplacePage = lazyWithRetry(() => import("./pages/MarketplacePage"));
const SavedDealsPage = lazyWithRetry(() => import("./pages/SavedDealsPage"));
const RedemptionsPage = lazyWithRetry(() => import("./pages/RedemptionsPage"));
const RedemptionDetailPage = lazyWithRetry(
  () => import("./pages/RedemptionDetailPage")
);
const PointsRedemptionPage = lazyWithRetry(
  () => import("./pages/PointsRedemptionPage")
);
const OutstandingPointsPage = lazyWithRetry(
  () => import("./pages/OutstandingPointsPage")
);
const PointsReversalsPage = lazyWithRetry(
  () => import("./pages/PointsReversalsPage")
);
const SetPasswordPage = lazyWithRetry(() => import("./pages/SetPasswordPage"));
const ForgotPasswordPage = lazyWithRetry(
  () => import("./pages/ForgotPasswordPage")
);
const CreativeEnginePage = lazyWithRetry(
  () => import("./pages/CreativeEnginePage")
);
const BoostPage = lazyWithRetry(() => import("./pages/BoostPage"));
const NetworkDealsPage = lazyWithRetry(
  () => import("./pages/NetworkDealsPage")
);
const CreateInterVenueDealPage = lazyWithRetry(
  () => import("./pages/CreateInterVenueDealPage")
);
const DealAnalyticsPage = lazyWithRetry(
  () => import("./pages/DealAnalyticsPage")
);
const CreateCampaignPage = lazyWithRetry(
  () => import("./pages/CreateCampaignPage")
);
const DPASigningPage = lazyWithRetry(() => import("./pages/DPASigningPage"));
const PrivacyPolicyPage = lazyWithRetry(
  () => import("./pages/PrivacyPolicyPage")
);
const TermsOfUsePage = lazyWithRetry(() => import("./pages/TermsOfUsePage"));
const PrivacySettingsPage = lazyWithRetry(
  () => import("./pages/PrivacySettingsPage")
);
const ShortlinkAnalyticsPage = lazyWithRetry(
  () => import("./pages/ShortlinkAnalyticsPage")
);

// Property Operator pages
const PropertyDashboardPage = lazyWithRetry(
  () => import("./pages/PropertyDashboardPage")
);
const PropertyMembersPage = lazyWithRetry(
  () => import("./pages/PropertyMembersPage")
);
const PropertyAnalyticsPage = lazyWithRetry(
  () => import("./pages/PropertyAnalyticsPage")
);
const PropertyQRCodesPage = lazyWithRetry(
  () => import("./pages/PropertyQRCodesPage")
);
const VenueDetailPage = lazyWithRetry(() => import("./pages/VenueDetailPage"));

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NavigationProgress />
        <RouteAnnouncer />
        <NewVersionBanner />
        <NewVersionModal />
        <VenueAuthProvider>
          <VenueProvider>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/verify-2fa" element={<Verify2FAPage />} />
                <Route path="/email-verified" element={<EmailVerifiedPage />} />
                <Route path="/set-password" element={<SetPasswordPage />} />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfUsePage />} />

                {/* DPA signing (owner only) — standalone (no header/sidebar) */}
                <Route
                  path="/dpa"
                  element={
                    <ProtectedRoute requiredRole="venue_owner">
                      <DPASigningPage />
                    </ProtectedRoute>
                  }
                />

                {/* 2FA Setup — standalone (no header/sidebar) */}
                <Route
                  path="/setup-2fa"
                  element={
                    <ProtectedRoute>
                      <Setup2FAPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected routes — venue console */}
                <Route
                  element={
                    <ProtectedRoute>
                      <VenueAccessGate>
                        <VenueLayout />
                      </VenueAccessGate>
                    </ProtectedRoute>
                  }
                >
                  {/* All staff: scanner + manager */}
                  <Route index element={<DashboardPage />} />
                  <Route path="qr-codes" element={<QRCodesPage />} />
                  <Route path="scanner" element={<ScannerPage />} />
                  <Route path="rewards" element={<RewardsPage />} />
                  <Route
                    path="transactions"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <CommissionDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route path="referrals" element={<ReferralDashboardPage />} /> */}
                  <Route path="marketplace" element={<MarketplacePage />} />
                  <Route
                    path="saved-deals"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <SavedDealsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="analytics"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <LocationAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="performance"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <VenueAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  {/*
                  <Route path="ai-studio" element={<AIStudioPage />} />
                  <Route path="content" element={<ProtectedRoute allowedStaffRoles={["manager"]}><ContentPage /></ProtectedRoute>} />
                  <Route path="content/new" element={<ProtectedRoute allowedStaffRoles={["manager"]}><CreatePostPage /></ProtectedRoute>} />
                  <Route path="content/:id" element={<ProtectedRoute allowedStaffRoles={["manager"]}><PostDetailPage /></ProtectedRoute>} />
                  <Route path="content/:id/edit" element={<ProtectedRoute allowedStaffRoles={["manager"]}><EditPostPage /></ProtectedRoute>} />
                  */}
                  <Route
                    path="invoices"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <InvoicesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="invoices/:id"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <InvoiceDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="disputes" element={<DisputesPage />} />
                  <Route
                    path="deals"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <DealsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="deals/new"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <CreateDealPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="deals/:id"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <DealDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="deals/:id/edit"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <EditDealPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="redemptions" element={<RedemptionsPage />} />
                  <Route
                    path="redemptions/pending"
                    element={<RedemptionsPage />}
                  />
                  <Route
                    path="redemptions/:id/verify"
                    element={<RedemptionDetailPage />}
                  />
                  <Route
                    path="dashboard/redemptions/:id"
                    element={<RedemptionDetailPage />}
                  />
                  <Route
                    path="points/redeem"
                    element={<PointsRedemptionPage />}
                  />
                  <Route
                    path="points/outstanding"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <OutstandingPointsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="points/reversals"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <PointsReversalsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="qr-codes/new" element={<CreateQRCodePage />} />

                  {/* New feature routes */}
                  <Route
                    path="creative-engine"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <CreativeEnginePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="boost"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <BoostPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="boost/create"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <CreateCampaignPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Network Deals routes */}
                  <Route
                    path="network-deals"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <NetworkDealsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="network-deals/create"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <CreateInterVenueDealPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route
                    path="redemptions/terminal"
                    element={<ProtectedRoute allowedStaffRoles={["manager"]}><DealsTerminalPage /></ProtectedRoute>}
                  /> */}
                  <Route
                    path="deals-analytics"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <DealAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="shortlink-analytics"
                    element={
                      <ProtectedRoute allowedStaffRoles={["manager"]}>
                        <ShortlinkAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Property Operator routes */}
                  <Route
                    path="property-dashboard"
                    element={
                      <ProtectedRoute requiredRole="property_operator">
                        <PropertyDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="property-members"
                    element={
                      <ProtectedRoute requiredRole="property_operator">
                        <PropertyMembersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="property-members/venues/:venueId"
                    element={
                      <ProtectedRoute requiredRole="property_operator">
                        <VenueDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="property-qr-codes"
                    element={
                      <ProtectedRoute requiredRole="property_operator">
                        <PropertyQRCodesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="property-analytics"
                    element={
                      <ProtectedRoute requiredRole="property_operator">
                        <PropertyAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Owner only */}
                  <Route
                    path="staff"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <StaffPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route
                    path="settings/dpa"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <DPASigningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="settings/2fa"
                    element={<Navigate to="/setup-2fa" replace />}
                  />
                  <Route
                    path="settings/privacy"
                    element={
                      <ProtectedRoute requiredRole="venue_owner">
                        <PrivacySettingsPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Alias routes for test compatibility */}
                <Route
                  element={
                    <ProtectedRoute>
                      <VenueAccessGate>
                        <VenueLayout />
                      </VenueAccessGate>
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path="dashboard"
                    element={<Navigate to="/" replace />}
                  />
                  <Route path="scan" element={<ScannerPage />} />
                  <Route
                    path="promotions"
                    element={<Navigate to="/deals" replace />}
                  />
                  <Route
                    path="promotions/create"
                    element={<Navigate to="/deals/new" replace />}
                  />
                  <Route
                    path="analytics/geolocation"
                    element={<LocationAnalyticsPage />}
                  />
                </Route>

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </VenueProvider>
        </VenueAuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
